import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import 'trix';
import 'trix/dist/trix.css';
import { patchRichiestaRimborso } from '../services/api_cost_refunds';
import Swal from 'sweetalert2';
import { AuthContext } from '../contexts/auth';
import moment from 'moment';

const FormEditRimborsoKmCompany = ({ afterSubmitEditRecord, recordToEdit, onCancelEdit }) => {
  const { token } = useContext(AuthContext);

  const [title, setTitle] = useState(recordToEdit.title || '');
  const [importo, setImporto] = useState(recordToEdit.amount || '');
  const [date, setDate] = useState(recordToEdit.date || '');
  const [file, setFile] = useState(recordToEdit.file || null);

  const [description, setDescription] = useState(recordToEdit?.description || '');
  const descriptionRef = useRef();

  const [vehicle, setVehicle] = useState('');
  const [departureAddress, setDepartureAddress] = useState('');
  const [arrivalAddress, setArrivalAddress] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState('');
  const [vehicleError, setVehicleError] = useState('');
  const [departureAddressError, setDepartureAddressError] = useState('');
  const [arrivalAddressError, setArrivalAddressError] = useState('');
  const [importoError, setImportoError] = useState('');


  useEffect(() => {
    const extractDetailsFromDescription = (description) => {
      const vehicleMatch = description.match(/Veicolo:\s*(.*?)(<br \/>|$)/);
      const departureMatch = description.match(/Indirizzo Partenza:\s*(.*?)(<br \/>|$)/);
      const arrivalMatch = description.match(/Indirizzo Arrivo:\s*(.*?)(<br \/>|$)/);
      const notesMatch = description.match(/Note Aggiuntive:\s*(.*?)(<br \/>|$)/);
  
      if (vehicleMatch) setVehicle(vehicleMatch[1].trim());
      if (departureMatch) setDepartureAddress(departureMatch[1].trim());
      if (arrivalMatch) setArrivalAddress(arrivalMatch[1].trim());
      if (notesMatch) {
        descriptionRef.current.value = notesMatch[1].trim(); 
        document.querySelector("trix-editor").editor.loadHTML(notesMatch[1].trim());
      }
    };
  
    if (recordToEdit?.description) {
      descriptionRef.current.value = recordToEdit.description;
      extractDetailsFromDescription(recordToEdit.description);
    }
  
    const trixEditor = document.querySelector("trix-editor");
    const handleTrixChange = (event) => {
      const newDescription = event.target.innerHTML;
      setDescription(newDescription);
      descriptionRef.current.value = newDescription;
    };
  
    trixEditor.addEventListener("trix-change", handleTrixChange);
    return () => {
      trixEditor.removeEventListener("trix-change", handleTrixChange);
    };
  }, [recordToEdit]);
 

  const updateDescription = () => {
    return `
      Veicolo: ${vehicle}<br />
      Indirizzo Partenza: ${departureAddress}<br />
      Indirizzo Arrivo: ${arrivalAddress}<br />
      Note Aggiuntive: ${descriptionRef.current.value}
    `;
  };

  const onDrop = useCallback(acceptedFiles => {
    const validFile = acceptedFiles.find(file => {
      if (file.size > 2 * 1024 * 1024) {
        alert('File troppo grande. Massimo 2mb');
        return false;
      }
      return true;
    });

    if (validFile) {
      setFile(validFile);
    }
  }, []);


  const { getRootProps, getInputProps } = useDropzone({ onDrop, multiple: false });

  const handleDeleteFile = () => {
    setFile(null);
  };

  const handleFileChange = (event) => {
    const newFile = event.target.files[0];
    setFile(newFile);
  };

  const handleImportoChange = (e) => {
    let value = e.target.value;

    if (!isNaN(value)) {
      const decimalIndex = value.indexOf('.');
      if (decimalIndex !== -1 && value.length - decimalIndex > 3) {
        value = value.substring(0, decimalIndex + 3);
      }

      setImporto(value);
    }
  };

  const handleDateChange = (e) => {
    let inputValue = e.target.value;

    const dateParts = inputValue.split('-');

    if (dateParts[0]?.length > 4) {
        dateParts[0] = dateParts[0].slice(0, 4);
        inputValue = dateParts.join('-');
    }

    setDate(inputValue);
  };

  const validateForm = () => {
    let valid = true;
  
    setFormError('');
    setImportoError('');
    setVehicleError('');
    setDepartureAddressError('');
    setArrivalAddressError('');

    if (!vehicle) {
      setVehicleError('Il veicolo è richiesto');
      valid = false;
    }

    if (!departureAddress) {
      setDepartureAddressError('L\'indirizzo di partenza è richiesto');
      valid = false;
    }

    if (!arrivalAddress) {
      setArrivalAddressError('L\'indirizzo di arrivo è richiesto');
      valid = false;
    }
  
    if (!importo) {
      setImportoError('I Km sono richiesti');
      valid = false;
    } else if (isNaN(importo) || parseFloat(importo) <= 0) {
      setImportoError('Inserisci un numero valido');
      valid = false;
    }
  
    return valid;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const updatedDescription = updateDescription();
    const updatedData = {
      title: recordToEdit.title,
      description: updatedDescription,
      amount: importo,
      date,
    };

    if (recordToEdit.file !== file) {
      updatedData.file = file;
    }

    setLoading(true);
    try {
      const updatedRecord = await patchRichiestaRimborso(token, recordToEdit.id, updatedData);
      await afterSubmitEditRecord(updatedRecord);

      await Swal.fire({
        title: 'Salvato!',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500,
      });

    } catch (error) {
      setError(error);

      await Swal.fire({
        title: 'Errore',
        text: 'Si è verificato un errore durante l\'aggiornamento',
        icon: 'error',
        confirmButtonText: 'OK',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleFileAccept = (event) => {
      event.preventDefault();
    };
  
    document.addEventListener("trix-file-accept", handleFileAccept);
  
    return () => {
      document.removeEventListener("trix-file-accept", handleFileAccept);
    };
  }, []);

  const css = `
    .trix-button.trix-button--icon.trix-button--icon-attach {
      display: none;
    }
    .trix-button-group.trix-button-group--file-tools {
    display: none;
    }
  `;

  return (
    <div className="mx-4 p-6 pt-2 shadow-md rounded-md bg-gray-50">
      <style>
        {css}
      </style>
      <h1 className='block text-gray-700 text-md font-bold mb-4'>Modifica Richiesta Rimborso Chilometrico</h1>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="date">
            Data
          </label>
          <input
            id="date"
            type="date"
            placeholder="Data"
            value={date}
            onChange={handleDateChange}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>

        <div className="mb-4">
          {
            recordToEdit.file && file && recordToEdit.file === file && (
            <div>
              <div className="mb-2">
                <label htmlFor="file" className='block text-gray-700 text-sm font-bold mb-2'>File caricato</label>
                <a href={recordToEdit.file} className="text-sm font-semibold bg-indigo-500 hover:bg-indigo-400 p-1 ml-3 text-white rounded-md inline-block" target="_blank" rel="noopener noreferrer">Visualizza</a>
                <button
                  type="button"
                  onClick={handleDeleteFile}
                  className="text-red-500 font-semibold ml-3"
                >
                  Elimina
                </button>
              </div>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="vehicle">
              Veicolo
            </label>
            <input
              id="vehicle"
              type="text"
              placeholder="Descrizione veicolo"
              value={vehicle}
              onChange={(e) => {
                setVehicle(e.target.value);
                setVehicleError('');
              }}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {vehicleError && <div className="text-red-500 text-sm mt-1">{vehicleError}</div>}
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="departureAddress">
              Indirizzo Partenza
            </label>
            <input
              id="departureAddress"
              type="text"
              placeholder="Indirizzo"
              value={departureAddress}
              onChange={(e) => {
                setDepartureAddress(e.target.value);
                setDepartureAddressError('');
              }}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {departureAddressError && <div className="text-red-500 text-sm mt-1">{departureAddressError}</div>}
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="arrivalAddress">
              Indirizzo Arrivo
            </label>
            <input
              id="arrivalAddress"
              type="text"
              placeholder="Indirizzo"
              value={arrivalAddress}
              onChange={(e) => {
                setArrivalAddress(e.target.value);
                setArrivalAddressError('');
              }}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            {arrivalAddressError && <div className="text-red-500 text-sm mt-1">{arrivalAddressError}</div>}
          </div>


          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="importo" >
              Chilometri
            </label>
            <input
              id="importo"
              type="number"
              value={importo}
              onChange={handleImportoChange}
              placeholder="Numero Chilometri"
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              step="0.01"
            />
            {importoError && <div className="text-red-500 text-sm mt-1">{importoError}</div>}
          </div>

          {recordToEdit.file !== file && file && (
            <div className="mb-4">
              <h2 className="text-lg font-bold mb-2">File che stai caricando:</h2>
              <div className="mb-2">
                <p><strong>Nome:</strong> {file.name}</p>
                <p><strong>Tipo:</strong> {file.type}</p>
                <p><strong>Dimensione:</strong> {(file.size / 1024).toFixed(2)} KB</p>
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                  }}
                  className="text-red-500 font-semibold">Annulla</button>
              </div>
            </div>
          )} 
          
          {!file && (
            <div {...getRootProps()} className="mb-4 border-dashed border-2 border-gray-300 p-4 text-center cursor-pointer" >
              <input {...getInputProps()} onChange={handleFileChange} />
              <p>Trascina qui il file o clicca per selezionare un file</p>
            </div>
          )}
        </div>
        
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
            Note aggiuntive
          </label>
          <input
            id="x"
            type="hidden"
            ref={descriptionRef}
          />
          <trix-editor input="x" style={{background: "white"}}></trix-editor>
        </div>

        <div className="flex items-center justify-between">
          {loading ? (
            <div className="text-sm font-semibold text-gray-900">Salvataggio in corso...</div>
          ) : (
            <>
              <button
                type="submit"
                className="bg-indigo-500 w-full text-white text-sm px-4 py-2 rounded-md hover:bg-indigo-400 focus:outline-none focus:bg-indigo-200 w-24 me-4 font-semibold"
              >
                Aggiorna
              </button>
              <button
                type="button"
                onClick={onCancelEdit}
                className="bg-gray-200 w-full text-gray-600 text-sm px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:bg-gray-300 w-24 font-semibold"
              >
                Annulla
              </button>
            </>
          )}
          </div>
      </form>
    </div>
  );
};

export default FormEditRimborsoKmCompany;
